import React, { useState, useEffect } from 'react';
import apiService from '../../services/api';
import {
  Box,
  Card,
  CardContent,
  Typography,
  TextField,
  Button,
  Alert,
  Grid,
  Divider,
  FormControl,
  InputLabel,
  Select,
  MenuItem
} from '@mui/material';

const EmailTest = () => {
  const [templates, setTemplates] = useState([]);
  const [templatesLoading, setTemplatesLoading] = useState(true);
  const [emailData, setEmailData] = useState({
    to: '',
    cc: '',
    bcc: '',
    template: '',
    parameters: {}
  });
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [testHistory, setTestHistory] = useState([]);

  // Load available templates
  useEffect(() => {
    loadTemplates();
  }, []);

  // Effect to set initial template and parameters when templates load
  useEffect(() => {
    if (templates.length > 0 && !emailData.template) {
      const firstTemplate = templates[0];
      console.log('Setting initial template:', firstTemplate);
      const initialParameters = getDefaultParameters(firstTemplate.name);
      console.log('Initial parameters:', initialParameters);
      setEmailData(prev => ({
        ...prev,
        template: firstTemplate.name,
        parameters: initialParameters
      }));
    }
  }, [templates, emailData.template]);

  const loadTemplates = async () => {
    try {
      console.log('Loading templates...');
      setTemplatesLoading(true);
      const data = await apiService.get('/email/templates/all');
      
      console.log('Templates response:', data);
      
      if (data.success) {
        console.log('Setting templates:', data.data);
        console.log('First template full structure:', JSON.stringify(data.data[0], null, 2));
        
        // Check if variables are properly loaded
        data.data.forEach((template, index) => {
          console.log(`Template ${index + 1}:`, template.name);
          console.log(`  Variables:`, template.variables);
          console.log(`  Variables type:`, typeof template.variables);
          console.log(`  Variables length:`, template.variables ? template.variables.length : 'undefined');
        });
        
        setTemplates(data.data);
        if (data.data.length > 0) {
          // Set first template as default
          const firstTemplate = data.data[0];
          console.log('Setting first template:', firstTemplate);
          console.log('First template variables:', firstTemplate.variables);
          console.log('First template body_html:', firstTemplate.body_html);
          console.log('First template description:', firstTemplate.description);
          
          // Initial template is set, parameters will be set by useEffect
        }
      }
    } catch (error) {
      console.error('Failed to load templates:', error);
    } finally {
      setTemplatesLoading(false);
    }
  };

  const getDefaultParameters = (templateName) => {
    console.log('Getting default parameters for template:', templateName);
    console.log('Available templates:', templates);
    
    // Find the selected template
    const selectedTemplate = templates.find(t => t.name === templateName);
    console.log('Selected template:', selectedTemplate);
    
    if (!selectedTemplate) {
      console.log('No template found, returning empty object');
      return {};
    }
    
    // Get variables directly from the variables column
    let variables = selectedTemplate.variables || [];
    console.log('Template variables from DB:', variables);
    
    // If variables is a string, try to parse it as JSON or comma-separated
    if (typeof variables === 'string') {
      try {
        // Try to parse as JSON first
        const parsed = JSON.parse(variables);
        if (Array.isArray(parsed)) {
          variables = parsed;
          console.log('Parsed variables from JSON string:', variables);
        } else {
          // Try comma-separated format
          variables = variables.split(',').map(v => v.trim()).filter(v => v);
          console.log('Parsed variables from comma-separated string:', variables);
        }
      } catch (e) {
        // If JSON parsing fails, try comma-separated
        variables = variables.split(',').map(v => v.trim()).filter(v => v);
        console.log('Parsed variables from comma-separated string:', variables);
      }
    }
    
    console.log('Final variables array:', variables);
    console.log('Variables type:', typeof variables);
    console.log('Variables length:', variables ? variables.length : 'undefined');
    
    // Create default values for each variable
    const defaults = {};
    if (variables && variables.length > 0) {
      variables.forEach(variable => {
        console.log('Processing variable:', variable);
        // Generate sensible default values based on variable name
        if (variable === 'var_name') {
          defaults[variable] = 'John Doe';
        } else if (variable === 'var_email') {
          defaults[variable] = 'user@example.com';
        } else if (variable === 'var_phone') {
          defaults[variable] = '+1234567890';
        } else if (variable === 'var_date1') {
          defaults[variable] = '2024-01-15';
        } else if (variable === 'var_time') {
          defaults[variable] = '2:00 PM';
        } else if (variable === 'var_service') {
          defaults[variable] = 'Hair Cut';
        } else if (variable === 'var_stylist') {
          defaults[variable] = 'Sarah Johnson';
        } else if (variable === 'var_duration') {
          defaults[variable] = '60';
        } else if (variable === 'var_price') {
          defaults[variable] = '50';
        } else if (variable === 'var_location') {
          defaults[variable] = '123 Main St';
        } else if (variable === 'var_confirmation_link') {
          defaults[variable] = 'https://clicklooks.com/confirm?token=abc123';
        } else {
          // Generic default for unknown variables
          defaults[variable] = `Sample ${variable.replace('var_', '').replace(/_/g, ' ')}`;
        }
      });
    } else {
      console.log('No variables to process');
    }
    
    console.log('Generated defaults:', defaults);
    return defaults;
  };

  const handleTemplateChange = async (templateName) => {
    console.log('Template changed to:', templateName);
    console.log('Available templates at change:', templates);
    
    // Find the selected template
    const selectedTemplate = templates.find(t => t.name === templateName);
    if (!selectedTemplate) {
      console.log('Template not found');
      return;
    }
    
    // If we have the template but need to fetch full details
    if (selectedTemplate.id && (!selectedTemplate.variables || selectedTemplate.variables.length === 0)) {
      try {
        console.log('Fetching full template details for ID:', selectedTemplate.id);
        const templateData = await apiService.get(`/email/templates/${selectedTemplate.id}`);
        if (templateData.success) {
          console.log('Full template data:', templateData.data);
          // Update the template in our local state
          setTemplates(prev => prev.map(t => 
            t.id === selectedTemplate.id ? templateData.data : t
          ));
          // Now get parameters with the updated template
          const newParameters = getDefaultParameters(templateName);
          setEmailData(prev => ({
            ...prev,
            template: templateName,
            parameters: newParameters
          }));
          return;
        }
      } catch (error) {
        console.error('Failed to fetch template details:', error);
      }
    }
    
    // Use existing template data
    const newParameters = getDefaultParameters(templateName);
    console.log('New parameters generated:', newParameters);
    
    setEmailData(prev => {
      const newData = {
        ...prev,
        template: templateName,
        parameters: newParameters
      };
      console.log('New email data:', newData);
      return newData;
    });
  };

  const handleParameterChange = (key, value) => {
    setEmailData(prev => ({
      ...prev,
      parameters: {
        ...prev.parameters,
        [key]: value
      }
    }));
  };

  const handleSendEmail = async () => {
    if (!emailData.to) {
      setResult({ success: false, message: 'Please enter recipient email' });
      return;
    }

    setLoading(true);
    setResult(null);

    try {
      const payload = {
        template: emailData.template,
        to: emailData.to,
        cc: emailData.cc ? emailData.cc.split(',').map(e => e.trim()) : [],
        bcc: emailData.bcc ? emailData.bcc.split(',').map(e => e.trim()) : [],
        parameters: emailData.parameters
      };

      console.log('Sending email with payload:', payload);

      const data = await apiService.post('/email/send', payload);
      setResult(data);

      if (data.success) {
        // Add to test history
        setTestHistory(prev => [{
          timestamp: new Date().toLocaleString(),
          template: emailData.template,
          recipient: emailData.to,
          success: true,
          message: data.message
        }, ...prev.slice(0, 9)]); // Keep last 10
      }

    } catch (error) {
      setResult({ 
        success: false, 
        message: 'Failed to send email', 
        error: error.message 
      });
    } finally {
      setLoading(false);
    }
  };

  const runPredefinedTest = (testType) => {
    const testData = {
      welcome: {
        template: 'Welcome Email',
        to: 'test@example.com',
        parameters: { name: 'Test User', phone: '+1234567890' }
      },
      appointment: {
        template: 'Appointment Confirmation Email',
        to: 'client@example.com',
        parameters: { 
          name: 'Test Client', 
          date: '2024-01-20', 
          time: '3:00 PM',
          service: 'Test Service',
          stylist: 'Test Stylist',
          duration: '45',
          price: '$75',
          location: 'Test Location',
          phone: '+1234567890'
        }
      }
    };
    
    const test = testData[testType];
    setEmailData(prev => ({
      ...prev,
      template: test.template,
      to: test.to,
      parameters: test.parameters
    }));
  };

  return (
    <Box sx={{ p: 3, maxWidth: 800, mx: 'auto' }}>
      <Typography variant="h4" gutterBottom>
        Email Service Test
      </Typography>
      
      <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
        Simple email testing - just enter recipient and parameters, then send!
      </Typography>
      
      {/* Debug info */}
      <Box sx={{ mb: 2, p: 2, bgcolor: 'grey.100', borderRadius: 1 }}>
        <Typography variant="body2" color="text.secondary">
          Debug: Templates loaded: {templates.length} | Loading: {templatesLoading.toString()} | Selected: {emailData.template || 'None'}
        </Typography>
        {emailData.template && (
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
            Current Template: {emailData.template} | Parameters: {Object.keys(emailData.parameters).length} | 
            Template Variables: {(() => {
              const template = templates.find(t => t.name === emailData.template);
              if (!template) return 'Template not found';
              if (typeof template.variables === 'string') {
                try {
                  const parsed = JSON.parse(template.variables);
                  return Array.isArray(parsed) ? parsed.join(', ') : template.variables;
                } catch (e) {
                  return template.variables;
                }
              }
              return Array.isArray(template.variables) ? template.variables.join(', ') : 'No variables';
            })()}
          </Typography>
        )}
      </Box>
      
      <Grid container spacing={3}>
        {/* Email Configuration */}
        <Grid item xs={12}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Email Configuration
              </Typography>
              
              <Grid container spacing={2}>
                <Grid item xs={12}>
                  <FormControl fullWidth>
                    <InputLabel>Email Template</InputLabel>
                    <Select
                      value={emailData.template}
                      onChange={(e) => handleTemplateChange(e.target.value)}
                      label="Email Template"
                      disabled={templatesLoading}
                    >
                      {templatesLoading ? (
                        <MenuItem disabled>Loading templates...</MenuItem>
                      ) : templates.length > 0 ? (
                        templates.map((template) => (
                          <MenuItem key={template.name} value={template.name}>
                            {template.name}
                          </MenuItem>
                        ))
                      ) : (
                        <MenuItem disabled>No templates available</MenuItem>
                      )}
                    </Select>
                  </FormControl>
                </Grid>
                
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="To Email"
                    value={emailData.to}
                    onChange={(e) => setEmailData(prev => ({ ...prev, to: e.target.value }))}
                    placeholder="recipient@example.com"
                  />
                </Grid>
                
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    label="CC (comma separated)"
                    value={emailData.cc}
                    onChange={(e) => setEmailData(prev => ({ ...prev, cc: e.target.value }))}
                    placeholder="cc1@example.com, cc2@example.com"
                  />
                </Grid>
                
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    label="BCC (comma separated)"
                    value={emailData.bcc}
                    onChange={(e) => setEmailData(prev => ({ ...prev, bcc: e.target.value }))}
                    placeholder="bcc@example.com"
                  />
                </Grid>
              </Grid>

              <Divider sx={{ my: 3 }} />
              
                      {emailData.template && (
          <>
            <Typography variant="h6" gutterBottom>
              Template: {emailData.template}
            </Typography>
            
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              {templates.find(t => t.name === emailData.template)?.description || 'No description available'}
            </Typography>
            
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Available Parameters: {Object.keys(emailData.parameters).join(', ')}
            </Typography>
            
            {/* Debug info */}
            <Box sx={{ mb: 2, p: 1, bgcolor: 'grey.100', borderRadius: 1 }}>
              <Typography variant="body2" color="text.secondary">
                Debug: Parameters count: {Object.keys(emailData.parameters).length} | 
                Parameters object: {JSON.stringify(emailData.parameters)} | 
                Template Variables: {(() => {
                  const template = templates.find(t => t.name === emailData.template);
                  if (!template) return 'Template not found';
                  if (typeof template.variables === 'string') {
                    try {
                      const parsed = JSON.parse(template.variables);
                      return Array.isArray(parsed) ? parsed.join(', ') : template.variables;
                    } catch (e) {
                      return template.variables;
                    }
                  }
                  return Array.isArray(template.variables) ? template.variables.join(', ') : 'No variables';
                })()}
              </Typography>
            </Box>
          </>
        )}
              
              <Grid container spacing={2}>
                {Object.entries(emailData.parameters).length > 0 ? (
                  Object.entries(emailData.parameters).map(([key, value]) => (
                    <Grid item xs={12} md={6} key={key}>
                      <TextField
                        fullWidth
                        label={key.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}
                        value={value}
                        onChange={(e) => handleParameterChange(key, e.target.value)}
                      />
                    </Grid>
                  ))
                ) : (
                  <Grid item xs={12}>
                    <Alert severity="info">
                      No parameters found for this template. The template may not contain any variables, or the variables are not properly formatted.
                    </Alert>
                  </Grid>
                )}
              </Grid>

              <Box sx={{ mt: 3, display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                <Button
                  variant="contained"
                  onClick={handleSendEmail}
                  disabled={loading || !emailData.to || !emailData.template}
                >
                  {loading ? 'Sending...' : 'Send Email'}
                </Button>
                
                <Button
                  variant="outlined"
                  onClick={() => runPredefinedTest('welcome')}
                >
                  Test Welcome
                </Button>
                
                <Button
                  variant="outlined"
                  onClick={() => runPredefinedTest('appointment')}
                >
                  Test Appointment
                </Button>
              </Box>
            </CardContent>
          </Card>
        </Grid>

        {/* Results */}
        <Grid item xs={12}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Test Results
              </Typography>
              
              {result && (
                <Alert 
                  severity={result.success ? 'success' : 'error'} 
                  sx={{ mb: 2 }}
                >
                  {result.message}
                  {result.error && (
                    <Box sx={{ mt: 1 }}>
                      <Typography variant="body2" component="div">
                        Error: {result.error}
                      </Typography>
                    </Box>
                  )}
                </Alert>
              )}

              {testHistory.length > 0 && (
                <Box>
                  <Typography variant="subtitle2" gutterBottom>
                    Test History (Last 10):
                  </Typography>
                  {testHistory.map((test, index) => (
                    <Alert 
                      key={index} 
                      severity={test.success ? 'success' : 'error'}
                      sx={{ mb: 1 }}
                    >
                      <Typography variant="body2">
                        <strong>{test.timestamp}</strong> - {test.message}
                        <br />
                        Template: {test.template} | Recipient: {test.recipient}
                      </Typography>
                    </Alert>
                  ))}
                </Box>
              )}
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    </Box>
  );
};

export default EmailTest;
